import axios from 'axios'

import { BaseRepository } from './BaseRepository'

interface ProgressEvent {
  loaded: number
  total: number
}

export interface Speaker {
  id: number | string
  name: string
}

export interface SpeechTranscript {
  uuid: string
  text?: string
  words?: WordSegment[]
  speakers?: Speaker[]
  query?: string
  answer?: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  created_at: string
  updated_at: string
  file_url?: string | null
}

export interface SpeechTranscriptQuestion {
  uuid: string
  query: string
  answer?: string | null
  status: 'pending' | 'processing' | 'completed' | 'failed'
  error_message?: string | null
  created_at: string
  updated_at: string
}

export interface WordSegment {
  text: string
  start_time: number
  end_time: number
  speaker_id?: number | null
}

export interface CreateTranscriptParams {
  file?: File
  url?: string
  audio_token?: string
  query?: string
}

export interface AskQuestionParams {
  query: string
}

class SpeechTranscriptRepository extends BaseRepository {
  async listTranscripts() {
    return this.httpClient.get<{ success: boolean; items: SpeechTranscript[] }>('/api/v2/speech-to-text')
  }

  async getTranscript(uuid: string) {
    return this.httpClient.get<{ success: boolean; item: SpeechTranscript }>(`/api/v2/speech-to-text/${uuid}`)
  }

  async createTranscript(params: CreateTranscriptParams, onUploadProgress?: (progressEvent: ProgressEvent) => void) {
    const formData = new FormData()

    if (params.file) {
      formData.append('file', params.file)
    }
    if (params.url) {
      formData.append('url', params.url)
    }
    if (params.audio_token) {
      formData.append('audio_token', params.audio_token)
    }
    if (params.query) {
      formData.append('query', params.query)
    }

    // Use axios directly for file upload with progress tracking
    const response = await axios.post('/api/v2/speech-to-text', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onUploadProgress
        ? (progressEvent) => {
            onUploadProgress({
              loaded: progressEvent.loaded || 0,
              total: progressEvent.total || 0,
            })
          }
        : undefined,
      validateStatus: () => true, // Don't throw on any status code
    })

    // Handle 401 redirects like the AxiosHttpClient does
    if (response.status === 401 || (response.status === 404 && response.request?.responseURL.indexOf('/users/sign_in') > -1)) {
      ;(window as any).onbeforeunload = null
      window.location.href = '/users/sign_in'
    }

    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    }
  }

  async updateTranscript(uuid: string, params: { words: WordSegment[]; speakers?: Speaker[] }) {
    return this.httpClient.patch<{ success: boolean; item: SpeechTranscript }>(`/api/v2/speech-to-text/${uuid}`, params)
  }

  async deleteTranscript(uuid: string) {
    return this.httpClient.delete(`/api/v2/speech-to-text/${uuid}`)
  }

  async askQuestion(uuid: string, params: AskQuestionParams) {
    return this.httpClient.post<{ success: boolean; item: SpeechTranscriptQuestion; error?: string }>(`/api/v2/speech-to-text/${uuid}/ask`, params)
  }

  async getQuestion(transcriptUuid: string, questionUuid: string) {
    return this.httpClient.get<{ success: boolean; item: SpeechTranscriptQuestion; error?: string }>(`/api/v2/speech-to-text/${transcriptUuid}/questions/${questionUuid}`)
  }
}

export const speechTranscriptRepository = new SpeechTranscriptRepository()
